import { HOST, PORT } from '../environment';
import { CLIENT_ID_QR_PREFIX, parseClientIdQrPayload, setClientId, getClientId, isNativeShellApp } from './clientId';
import { ENERGY_CARD_QR_PREFIX, parseEnergyCardQrPayload } from './energyCard';

/**
 * Extract Toka station number from toka.energy URL path.
 * Returns the full numeric segment (e.g. https://toka.energy/app/61250/ -> "61250").
 */
export function getTokaNumberFromUrl(url) {
  try {
    const u = new URL(String(url).trim());
    const pathSegments = u.pathname.split('/').filter(Boolean);
    const lastSegment = pathSegments[pathSegments.length - 1];
    const num = parseInt(lastSegment, 10);
    if (!Number.isFinite(num) || num < 0) return null;
    return String(num);
  } catch {
    return null;
  }
}

/** Parse 220-km station/port number from QR payload (URL or loose text). */
export function getStationNumberFromQr(raw) {
  const value = String(raw ?? '').trim();
  if (!value) return null;

  try {
    const u = new URL(value);
    const station = u.searchParams.get('station') || u.searchParams.get('port');
    if (station && /^\d+$/.test(station)) return station;
  } catch {
    // not a full URL — fall through
  }

  const stationMatch = value.match(/[?&#](?:station|port)=(\d+)/i);
  if (stationMatch) return stationMatch[1];

  // Legacy path-style QR: .../start?station=123 or segments containing station=123
  const parts = value.split('/');
  for (const part of parts) {
    if (part.includes('station=')) {
      const [, stationNumber] = part.split('=');
      if (stationNumber && /^\d+$/.test(stationNumber)) return stationNumber;
    }
  }

  if (/^\d{1,6}$/.test(value)) return value;
  return null;
}

const START_MANY_PORTS_RE = /^\d{1,6}(?:\s*,\s*\d{1,6})*$/;

/** Parse /startMany?ports=634,635 from a scanned QR payload. */
export function getStartManyPathFromQr(raw) {
  const value = String(raw ?? '').trim();
  if (!value) return null;

  const toPath = portsRaw => {
    const ports = String(portsRaw || '')
      .split(',')
      .map(p => p.trim())
      .filter(p => /^\d{1,6}$/.test(p));
    return ports.length ? `/startMany?ports=${ports.join(',')}` : null;
  };

  try {
    const u = new URL(value, 'https://220-km.com');
    if (/\/startMany\/?$/i.test(u.pathname)) {
      return toPath(u.searchParams.get('ports'));
    }
  } catch {
    // fall through to loose match
  }

  const loose = value.match(/startMany\?ports=([^&#]+)/i);
  if (loose && START_MANY_PORTS_RE.test(loose[1].trim())) {
    return toPath(loose[1]);
  }

  return null;
}

/**
 * Resolve a scanned QR string into a navigation/action result.
 * Handles: family client-id QR, Energy Card QR, Toka URL, startMany share URL, 220 port/station QR.
 *
 * @returns {Promise<
 *   | { type: 'redirect'; href: string }
 *   | { type: 'navigate'; path: string }
 *   | { type: 'energyCardAlreadyScanned' }
 *   | { type: 'energyCardRequiresNativeApp' }
 *   | { type: 'error'; messageKey: string; tokaNumber?: string; url?: string }
 * >}
 */
export async function resolveScannedQrPayload(raw) {
  const url = String(raw ?? '').trim();
  if (!url) {
    return { type: 'error', messageKey: 'qrScanner.loadFromPhotoError' };
  }

  const importedClientId = parseClientIdQrPayload(url);
  if (url.startsWith(CLIENT_ID_QR_PREFIX) || importedClientId) {
    if (!importedClientId || !setClientId(importedClientId)) {
      return { type: 'error', messageKey: 'qrScanner.clientIdInvalid' };
    }
    return { type: 'redirect', href: '/history' };
  }

  const energyCardToken = parseEnergyCardQrPayload(url);
  if (energyCardToken || url.startsWith(ENERGY_CARD_QR_PREFIX)) {
    const token = energyCardToken || url.slice(ENERGY_CARD_QR_PREFIX.length).trim();
    if (!token) {
      return { type: 'error', messageKey: 'qrScanner.energyCardInvalid' };
    }

    // Energy Card activation is native-app only (same policy as sticker copy).
    if (!isNativeShellApp()) {
      return { type: 'energyCardRequiresNativeApp' };
    }

    try {
      const res = await fetch(
        `${HOST}:${PORT}/api/energy-card/activate?qrToken=${encodeURIComponent(token)}&clientUiId=${getClientId()}`,
        { method: 'POST' }
      );
      if (!res.ok) {
        const msg = await res.text();
        throw new Error(msg || 'activate failed');
      }
      const body = await res.json().catch(() => ({}));
      if (body?.alreadyActivated) {
        return { type: 'energyCardAlreadyScanned' };
      }
      return { type: 'redirect', href: '/history' };
    } catch (err) {
      console.error('Energy card activate failed', err);
      return { type: 'error', messageKey: 'qrScanner.energyCardActivateError' };
    }
  }

  if (url.includes('toka.energy')) {
    const tokaNumber = getTokaNumberFromUrl(url);
    if (!tokaNumber) {
      return {
        type: 'error',
        messageKey: 'qrScanner.invalidTokaUrl',
        tokaNumber: '',
        url,
      };
    }

    try {
      const res = await fetch(
        `${HOST}:${PORT}/api/device/v2/ports-by-toka?toka_number=${encodeURIComponent(tokaNumber)}`
      );
      const data = await res.json();
      const portNumbers = data?.portNumbers || [];
      if (portNumbers.length > 0) {
        return { type: 'navigate', path: `/startMany?ports=${portNumbers.join(',')}` };
      }
      return {
        type: 'error',
        messageKey: 'qrScanner.tokaNotFound',
        tokaNumber,
        url,
      };
    } catch (err) {
      console.error('Toka ports lookup failed', err);
      return {
        type: 'error',
        messageKey: 'qrScanner.tokaLookupError',
        tokaNumber: tokaNumber || '',
        url,
      };
    }
  }

  const startManyPath = getStartManyPathFromQr(url);
  if (startManyPath) {
    return { type: 'navigate', path: startManyPath };
  }

  const stationNumber = getStationNumberFromQr(url);
  if (stationNumber) {
    return { type: 'navigate', path: `/start?station=${stationNumber}` };
  }

  return { type: 'error', messageKey: 'qrScanner.loadFromPhotoError' };
}

export async function scanQrPayloadFromImageFile(file) {
  const QrScanner = (await import('qr-scanner')).default;

  const tryScan = async imageOrCanvas => {
    try {
      const result = await QrScanner.scanImage(imageOrCanvas, {
        returnDetailedScanResult: true,
        alsoTryWithoutScanRegion: true,
      });
      const payload = typeof result === 'string' ? result : result?.data;
      return payload ? String(payload).trim() : null;
    } catch {
      return null;
    }
  };

  const direct = await tryScan(file);
  if (direct) return direct;

  // Screenshots of sticker pages often need upscale / contrast / crop before jsQR succeeds,
  // especially for older dotted Energy Card PNGs still saved in galleries.
  const bitmap = await createImageBitmap(file);
  try {
    const variants = [];

    const pushScaled = (source, sw, sh, sx, sy) => {
      for (const scale of [1, 1.5, 2, 2.5, 3]) {
        variants.push({ source, sw, sh, sx, sy, scale });
      }
    };

    pushScaled(bitmap, bitmap.width, bitmap.height, 0, 0);

    // Center square — common phone screenshot framing
    const side = Math.min(bitmap.width, bitmap.height);
    pushScaled(bitmap, side, side, (bitmap.width - side) / 2, (bitmap.height - side) / 2);

    // Right half — Energy Card sticker puts QR on the right of the kWh/ports layout
    pushScaled(bitmap, Math.ceil(bitmap.width / 2), bitmap.height, Math.floor(bitmap.width / 2), 0);

    for (const variant of variants) {
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(variant.sw * variant.scale));
      canvas.height = Math.max(1, Math.round(variant.sh * variant.scale));
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) continue;
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(variant.source, variant.sx, variant.sy, variant.sw, variant.sh, 0, 0, canvas.width, canvas.height);

      const found = await tryScan(canvas);
      if (found) return found;

      // High-contrast binary pass helps when the photo is washed out.
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const { data } = imageData;
      for (let i = 0; i < data.length; i += 4) {
        const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
        const v = gray > 140 ? 255 : 0;
        data[i] = v;
        data[i + 1] = v;
        data[i + 2] = v;
      }
      ctx.putImageData(imageData, 0, 0);
      const contrastFound = await tryScan(canvas);
      if (contrastFound) return contrastFound;
    }
  } finally {
    bitmap.close?.();
  }

  return null;
}
